import React, { useCallback, useEffect } from "react";
import { RecruiterActivity } from "redux/models/recruiterActivity";
import { useAPIRequest } from "hooks/useAPIRequest";
import { Tooltip } from "common/components/tooltip/tooltip";
import { InfoIcon } from "common/icons/InfoIcon";
import { Divider } from "antd";
import { Section } from "../../components/section";
import { SectionError } from "../../components/sectionError";
import { BillingProgress } from "./billingProgress/billingProgress";
import { GoalMetrics } from "./goalMetrics/goalMetrics";
import * as styles from "./monthlyGoals.module.scss";

const INFO_TEXT =
  "Billing and payout amounts may change after month-end processing due to recruiter assignments, discounts, or other adjustments.";

const MonthlyGoalsComponent = ({ clientId, recruiterId }) => {
  const fetchGoalProgress = useCallback(
    () => RecruiterActivity.monthlyProgress(clientId, recruiterId, false),
    [clientId, recruiterId],
  );

  const { data, loading, error, execute } = useAPIRequest(fetchGoalProgress, {
    initialLoading: true,
  });

  useEffect(() => {
    if (clientId && recruiterId) {
      execute();
    }
  }, [clientId, recruiterId, execute]);

  const renderContent = () => {
    if (!clientId || !recruiterId) {
      return (
        <div className={styles.monthlyGoals__emptyState}>
          Please select a client and recruiter to view monthly goal progress.
        </div>
      );
    }

    if (error && !loading) {
      return <SectionError message={error.message} onRetry={execute} />;
    }

    const totalBilling = data?.totalBilling || 0;
    const billingGoal = data?.billingGoal || 0;
    const totalHires = data?.totalHires || 0;
    const estimatedCommission = data?.estimatedCommission || 0;
    const isEligible = totalBilling >= billingGoal && estimatedCommission > 0;

    return (
      <div className={styles.monthlyGoals}>
        <div className={styles.monthlyGoals__billingProgress}>
          <BillingProgress
            totalBilling={totalBilling}
            billingGoal={billingGoal}
            loading={loading}
          />
        </div>
        <Divider style={{ margin: 0 }} />
        <div className={styles.monthlyGoals__metrics}>
          <GoalMetrics
            clientId={clientId}
            recruiterId={recruiterId}
            totalHires={totalHires}
            estimatedCommission={estimatedCommission}
            isEligible={isEligible}
            loading={loading}
          />
        </div>
      </div>
    );
  };

  return (
    <Section
      title="Revenue & Hiring Overview"
      headerClassName={styles.monthlyGoals__header}
      titleAlign="center"
      extra={
        <Tooltip title={INFO_TEXT} placement="left">
          <InfoIcon
            className={`text-primary-accent ${styles.monthlyGoals__infoIcon}`}
          />
        </Tooltip>
      }
    >
      {renderContent()}
    </Section>
  );
};

export const MonthlyGoals = React.memo(MonthlyGoalsComponent);
